import { NgClass } from "@angular/common";
import { Component, Input } from "@angular/core";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { CreateNewsPostRequest, CreateNewsPostResponse, DataResponse, GetNewsPostResponse, NewsPost, NewsPostStub, UpdateNewsPostRequest } from "@apiserver-rest/ponder-data-client-base";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { take } from "rxjs";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { NewsPostComponent } from "@ponder-features/news/news-post.component";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "news-post-editor",
    imports: [NgClass, LoadingComponent, BusySpinComponent, ReactiveFormsModule, NewsPostComponent],
    templateUrl: "./news-post-editor.component.html"
})
export class AdminNewsPostEditorComponent {
    @Input({ required: true }) public username!: string;
    @Input() public stub?: NewsPostStub;
    public post?: NewsPost;

    public isLoading = false;
    public loadingSuccess = true;

    public isProcessing: boolean = false;
    public processingError: boolean = false;
    public showPreview: boolean = false;
    public postForm!: FormGroup;

    public titlePreview: string = "";
    public bodyPreview: string = "";

    public authorUsername!: string;
    public postCreatedDate!: Date;
    public postModifiedDate?: Date;

    public constructor(formBuilder: FormBuilder, private ponderDataClient: PonderDataClient, private events: EventBroker, private navigation: NavigationService) {
        this.postForm = formBuilder.group({
            title: ["", [Validators.required, Validators.minLength(3)]],
            body: ["", [Validators.required, Validators.minLength(3)]]
        });
    }

    public ngOnInit(): void {
        this.fetchData();
    }

    public ngOnChanges(): void {
        this.fetchData();
    }

    private fetchData(): void {
        this.postForm.setValue({
            title: "",
            body: ""
        });

        this.authorUsername = this.username;
        this.postCreatedDate = new Date();
        this.postModifiedDate = undefined;

        if (this.stub) {
            if (this.isLoading) {
                return;
            }
    
            this.isLoading = true;
            this.loadingSuccess = false;
    
            this.ponderDataClient
                .newsPostGet(this.stub.id)
                .pipe(take(1))
                .subscribe({
                    next: (response: GetNewsPostResponse) => {
                        if (response.success && response.post) {
                            this.loadingSuccess = true;
                            this.post = response.post;

                            this.postForm.setValue({
                                title: this.post?.title,
                                body: this.post?.content
                            });

                            this.authorUsername = this.post.author.username!;
                            this.postCreatedDate = this.post.createdAt;
                            this.postModifiedDate = new Date();
                        }
                    }
                })
                .add(() => this.isLoading = false );
        } else {
            this.post = undefined;
        }
    }

    public canSubmit(): boolean {
        return this.postForm.valid && !this.isProcessing;
    }

    public onSubmit(): void {
        if (!this.canSubmit()) {
            return;
        }

        this.isProcessing = true;
        this.processingError = false;

        if (this.post) {
            this.submitPostUpdate();
        } else {
            this.submitNewPost();
        }
    }

    private submitNewPost(): void {
        var request = new CreateNewsPostRequest({
            title: (this.postForm.get("title")!.value as string).trim(),
            content: (this.postForm.get("body")!.value as string).trim()
        });

        this.ponderDataClient
            .newsPostPost(request)
            .pipe(take(1))
            .subscribe({
                next: (response: CreateNewsPostResponse) => {
                    if (response.success) {
                        this.navigation.reload();
                    } else {
                        this.processingError = true;
                    }
                },
                error: () => {
                    this.processingError = true;
                }
            })
            .add(() => this.isProcessing = false);
    }

    private submitPostUpdate(): void {
        this.post!.title = this.getTitle();
        this.post!.content = (this.postForm.get("body")!.value as string).trim();
        
        var request = new UpdateNewsPostRequest({
            post: this.post!
        });

        this.ponderDataClient
            .newsPostPatch(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (response.success) {
                        this.navigation.reload();
                    } else {
                        this.processingError = true;
                    }
                },
                error: () => {
                    this.processingError = true;
                }
            })
            .add(() => this.isProcessing = false);
    }

    public onTogglePreview(): void {
        this.showPreview = !this.showPreview;

        if (this.showPreview) {
            this.titlePreview = this.getTitle();
            this.bodyPreview = this.getBody();

            if (this.titlePreview.length == 0) {
                this.titlePreview = "(empty)";
            }

            if (this.bodyPreview.length == 0) {
                this.bodyPreview = "(empty)";
            }
        }
    }

    private getTitle(): string {
        return (this.postForm.get("title")?.value as string | undefined)?.trim() ?? "";
    }

    private getBody(): string {
        return (this.postForm.get("body")?.value as string | undefined)?.trim() ?? "";
    }

    public onCancelClicked(): void {
        this.post = undefined;
        this.showPreview = false;
        this.events.ui.onEditCancelled.emit();
    }
}